/**
 * Design System - Card Component
 *
 * Flexible card component for displaying content
 */

import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../utils/cn';

const cardVariants = cva(
  'rounded-xl border bg-white transition-all duration-300',
  {
    variants: {
      variant: {
        default: 'border-[#E7E5E4]',
        elevated: 'border-[#E7E5E4] shadow-[4px_4px_4px_0px_rgba(0,0,0,0.1)]',
        outlined: 'border-2 border-[#D6D3D1]',
        ghost: 'border-transparent',
      },
      padding: {
        none: 'p-0',
        sm: 'p-3',
        md: 'p-6',
        lg: 'p-8',
      },
      hoverable: {
        true: 'hover:shadow-md hover:scale-[1.02] cursor-pointer',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      padding: 'md',
      hoverable: false,
    },
  }
);

export interface CardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {
  /**
   * Card header content
   */
  header?: React.ReactNode;
  /**
   * Card footer content
   */
  footer?: React.ReactNode;
  /**
   * Card body content
   */
  children?: React.ReactNode;
}

export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  (
    {
      className,
      variant,
      padding,
      hoverable,
      header,
      footer,
      children,
      ...props
    },
    ref
  ) => {
    return (
      <div
        className={cn(cardVariants({ variant, padding, hoverable, className }))}
        ref={ref}
        {...props}
      >
        {header && (
          <div className='border-b border-[#E7E5E4] pb-4 mb-4'>{header}</div>
        )}

        {children}

        {footer && (
          <div className='border-t border-[#E7E5E4] pt-4 mt-4'>{footer}</div>
        )}
      </div>
    );
  }
);

Card.displayName = 'Card';

/**
 * Image Card - Card with image at the top
 */
export interface ImageCardProps extends CardProps {
  /**
   * Image source URL
   */
  imageSrc: string;
  /**
   * Image alt text
   */
  imageAlt?: string;
  /**
   * Image aspect ratio
   */
  imageAspectRatio?: 'square' | 'video' | 'wide';
}

export const ImageCard = React.forwardRef<HTMLDivElement, ImageCardProps>(
  (
    {
      className,
      imageSrc,
      imageAlt = '',
      imageAspectRatio = 'video',
      children,
      ...props
    },
    ref
  ) => {
    const aspectRatioClasses = {
      square: 'aspect-square',
      video: 'aspect-video',
      wide: 'aspect-[21/9]',
    };

    return (
      <Card
        className={cn('overflow-hidden', className)}
        padding='none'
        ref={ref}
        {...props}
      >
        <div
          className={cn(
            'w-full overflow-hidden',
            aspectRatioClasses[imageAspectRatio]
          )}
        >
          {/* eslint-disable-next-line @next/next/no-img-element -- card image, dynamic src */}
          <img
            src={imageSrc}
            alt={imageAlt}
            className='w-full h-full object-cover'
          />
        </div>
        {children && <div className='p-6'>{children}</div>}
      </Card>
    );
  }
);

ImageCard.displayName = 'ImageCard';
